/**
 * Typed wrappers for the custom (non-CRUD) super-admin User endpoints:
 * detail (with assignments), and the lifecycle verbs deactivate / reactivate
 * / purge.
 *
 * List / invite (create) / update go through Refine's dataProvider.
 *
 * The `deactivateUser` and `purgeUser` signatures are deliberately shaped to
 * satisfy the injected-callback contracts of `@viq-admin/ui`'s
 * DeactivateUserModal (`onDeactivate(oid, reason)`) and PurgeUserModal
 * (`onPurge(oid, { confirmation, reason })`) AND the real backend bodies —
 * deactivate posts `{ reason }`, purge DELETEs `{ confirmation, reason }`
 * (confirmation must be "DELETE", reason ≥ 10 chars; the backend re-validates).
 */
import type { ConferenceRef, User } from '@viq-admin/types';
import { superadminFetch } from './dataProvider';

/**
 * A user plus its conference assignments + company name, as returned by
 * GET /v1/superadmin/users/{oid} (mirrors the backend UserWithAssignments).
 */
export interface UserWithAssignments extends User {
  conferenceIds: string[];
  conferences: ConferenceRef[];
  companyName: string | null;
}

/** GET /v1/superadmin/users/{oid}. */
export async function getUser(oid: string): Promise<UserWithAssignments> {
  return superadminFetch<UserWithAssignments>(
    `/v1/superadmin/users/${oid}`,
  );
}

/** POST /v1/superadmin/users/{oid}/deactivate — body `{ reason }`. */
export async function deactivateUser(
  oid: string,
  reason: string,
): Promise<void> {
  await superadminFetch<void>(`/v1/superadmin/users/${oid}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** POST /v1/superadmin/users/{oid}/reactivate — no body. */
export async function reactivateUser(oid: string): Promise<void> {
  await superadminFetch<void>(`/v1/superadmin/users/${oid}/reactivate`, {
    method: 'POST',
  });
}

/**
 * DELETE /v1/superadmin/users/{oid} — permanent purge.
 * Body carries the typed confirmation + reason (matches PurgeUserModal's
 * `onPurge(oid, { confirmation, reason })` and the backend's guard).
 */
export async function purgeUser(
  oid: string,
  body: { confirmation: string; reason: string },
): Promise<void> {
  await superadminFetch<void>(`/v1/superadmin/users/${oid}`, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}
